import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { sdkTypes } from '@vansite/ts-sharetribe-flex-sdk';
import Decimal from 'decimal.js';
import React from 'react';
import { useIntl } from 'react-intl';

import config from '../../../config';
import { formatMoney } from '../../../util/currency';
import { FormattedMessage } from '../../../util/reactIntl';
import {
  LINE_ITEM_ANNUAL_PASS_VOUCHER,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_CUSTOMER_COMMISSION_EXTRA_LINE_ITEMS,
  LINE_ITEM_LISTING_CONTINGENT,
  LINE_ITEM_NIGHT,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_PROVIDER_NIGHTLY_COMMISSION,
  LINE_ITEM_VOUCHER,
} from '../../../util/types';
import { itemValueStyles, subTotalLineItemStyles, totalDividerStyles } from './BookingBreakdown.styles';

const { Money } = sdkTypes;

/**
 * Calculates the total price in sub units for multiple line items.
 */
const lineItemsTotal = (lineItems) => {
  const amount = lineItems.reduce((total, item) => {
    return total + item.lineTotal.amount;
  }, 0);
  const currency = lineItems[0] ? lineItems[0].lineTotal.currency : config.currency;
  return new Money(new Decimal(amount).round().toNumber(), currency);
};

/**
 * Checks if line item represents commission
 */
const isCommission = (lineItem) => {
  return (
    lineItem.code === LINE_ITEM_PROVIDER_COMMISSION ||
    lineItem.code === LINE_ITEM_PROVIDER_NIGHTLY_COMMISSION ||
    lineItem.code === LINE_ITEM_CUSTOMER_COMMISSION ||
    lineItem.code === LINE_ITEM_CUSTOMER_COMMISSION_EXTRA_LINE_ITEMS
  );
};

/**
 * Checks if line item represents commission
 */
const isVoucher = (lineItem) => {
  return lineItem.code === LINE_ITEM_VOUCHER;
};

/**
 * Returns non-commission, non-reversal line items
 */
const nonCommissionNonReversalLineItems = (lineItems, userRole) => {
  return lineItems.filter(
    (item) =>
      !isCommission(item) &&
      !isVoucher(item) &&
      !item.reversal &&
      item.unitPrice.amount > 0 &&
      item.includeFor.includes(userRole),
  );
};

/**
 * Checks if a transaction has a commission line-item for
 * a given user role.
 */
const txHasCommission = (lineItems, userRole) => {
  let commissionLineItem = null;

  if (userRole === 'customer') {
    commissionLineItem = lineItems.find(
      (item) => item.code === LINE_ITEM_CUSTOMER_COMMISSION,
    );
  } else if (userRole === 'provider') {
    commissionLineItem = lineItems.find(
      (item) =>
        item.code === LINE_ITEM_PROVIDER_COMMISSION ||
        item.code === LINE_ITEM_PROVIDER_NIGHTLY_COMMISSION,
    );
  }
  return !!commissionLineItem;
};

/**
 * @param {Object} props
 * @param {Array} props.lineItems line items to sum up, see adjustLineItemsForDisplay
 * @param {'customer'|'provider'} props.userRole role the breakdown is rendered for
 */
const LineItemSubTotalMaybe = ({ lineItems, userRole }) => {
  const intl = useIntl();

  const refund = lineItems.find(
    (item) =>
      (item.code === LINE_ITEM_NIGHT && item.reversal) ||
      (item.code === LINE_ITEM_NIGHT && item.lineTotal.amount < 0),
  );

  // Show unit purchase line total (unit price * quantity) as a subtotal.
  // PLEASE NOTE that this assumes that the transaction doesn't have other
  // line item types than the defined unit type (e.g. week, month, year).
  const showSubTotal = txHasCommission(lineItems, userRole) || refund;

  // all non-reversal, non-commission line items
  let subTotalLineItems = nonCommissionNonReversalLineItems(lineItems, userRole);

  if (userRole === 'provider') {
    // we need to remove the night line item if the user had a listing contingent line item
    const lineItemListingContingent = lineItems.find(
      (item) => item.code === LINE_ITEM_LISTING_CONTINGENT,
    );
    if (lineItemListingContingent) {
      subTotalLineItems = subTotalLineItems.filter(
        (item) => item.code !== LINE_ITEM_NIGHT && item.code !== LINE_ITEM_LISTING_CONTINGENT,
      );
    }
  }

  if (userRole === 'customer') {
    // we need to remove the night line item if the user had used an annual pass voucher
    const lineItemAnnualPass = lineItems.find(
      (item) => item.code === LINE_ITEM_ANNUAL_PASS_VOUCHER,
    );
    if (lineItemAnnualPass) {
      subTotalLineItems = subTotalLineItems.filter(
        (item) => item.code !== LINE_ITEM_ANNUAL_PASS_VOUCHER,
      );
    }
  }

  // line totals of those line items combined
  const subTotal = lineItemsTotal(subTotalLineItems);

  const formattedSubTotal = subTotalLineItems.length > 0 ? formatMoney(intl, subTotal) : null;

  return formattedSubTotal && showSubTotal ? (
    <>
      <Divider sx={totalDividerStyles} />
      <Box sx={subTotalLineItemStyles}>
        <Typography fontWeight="bold" sx={{ flex: 1 }} variant="lineItemLabel">
          <FormattedMessage id="BookingBreakdown.subTotal" />
        </Typography>
        <Typography fontWeight="bold" sx={itemValueStyles} variant="lineItemLabel">{formattedSubTotal}</Typography>
      </Box>
    </>
  ) : null;
};

export default LineItemSubTotalMaybe;
